import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../sever/Api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Check authentication status on app load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      const userId = localStorage.getItem('userId');


        if (token && userData && userId) {
          // You can also verify token with backend here
          setIsAuthenticated(true);
          setUser(JSON.parse(userData));
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
  
    } catch (error) {
      console.error('Error checking auth status:', error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData, userId) => {
    try {
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      const id = userId || (userData && (userData._id || userData.id));
      if (id) {
        localStorage.setItem('userId', id);
      }

      setIsAuthenticated(true);
      setUser(userData);

      // Don't redirect here - let the calling component handle redirect
      // navigate('/dashboard');
    } catch (error) {
      console.error('Error during login:', error);
    }
  };

  const logout = () => {
    try {
      // Clear all authentication data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userId');

      // Clear any other user-related data
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('johari_') || key.startsWith('test_') || key.startsWith('user_'))) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Update state
      setIsAuthenticated(false);
      setUser(null);

      // Redirect to home page and replace history entry
      navigate('/', { replace: true });

      // Force page reload to clear any cached state and prevent bfcache issues
      window.location.reload();
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const updateUser = (userData) => {
    try {
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const isTokenValid = () => {
    const token = localStorage.getItem('token');
    if (!token) return false;

    // You can add token expiration check here
    // For now, just check if token exists
    return true;
  };

  const requireAuth = (callback) => {
    if (!isAuthenticated || !isTokenValid()) {
      // Clear any invalid data
      logout();
      return false;
    }
    return true;
  };

  // Prevent back button access after logout
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!isAuthenticated) {
        // Clear any remaining data
        localStorage.clear();
      }
    };

    const handlePopState = () => {
      if (!isAuthenticated) {
        // If user tries to go back and is not authenticated, redirect to home
        navigate('/');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isAuthenticated, navigate]);

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    updateUser,
    requireAuth,
    isTokenValid,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
